import * as vp from 'vopay';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TextFieldPresets, Icons } from 'vopay';

import type { FieldData } from './types';
import type { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';

const { Box, ErrorText, Icon, Select, TextField, Toggle, GoogleAddressField } = vp;

export function createCurrencyField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): React.JSX.Element {
  return createTextField(fieldData, ctx, 'currency', 'dollar');
}

export function createEmailField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): React.JSX.Element {
  return createTextField(fieldData, ctx, 'email');
}

export function createNumberField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  icon?: Icons,
  handleBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void,
  onRefChange?: (ref: React.RefObject<HTMLInputElement | null>) => void,
  handleFieldChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
): React.JSX.Element {
  return createTextField(fieldData, ctx, 'numericWholePositive', icon, handleBlur, onRefChange, handleFieldChange);
}

export function createNumberFieldLead<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  icon?: Icons,
  handleBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void,
  onRefChange?: (ref: React.RefObject<HTMLInputElement | null>) => void,
  handleFieldChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
): React.JSX.Element {
  return createTextField(fieldData, ctx, 'number', icon, handleBlur, onRefChange, handleFieldChange);
}

export function createErrorMessage<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): React.JSX.Element {
  const errorMessage = ctx.errors[fieldData.fieldName] as string;
  return errorMessage ? (
    <Box key={`${fieldData.fieldName}-error-message`}>
      <Icon icon="asterisk" size="xsm" color="error" />
      <ErrorText error={errorMessage} />
    </Box>
  ) : (
    <div key={`no-${fieldData.fieldName}-error-message`} />
  );
}

export function createPercentageField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): React.JSX.Element {
  return createTextField(fieldData, ctx, 'numeric', 'percentage');
}
export type SelectOptions = {
  noOrdering?: boolean;
};
export function createSelectField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  options?: SelectOptions,
  handleFieldChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
): React.JSX.Element {
  return (
    <Select
      variant="plain"
      allowEmpty
      placeholder={fieldData.placeholder || ''}
      options={fieldData.getSelectOptions ? fieldData.getSelectOptions(ctx) : {}}
      name={fieldData.fieldName}
      onBlur={ctx.handleBlur}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        ctx.handleFieldChange(event);
        if (handleFieldChange !== undefined) handleFieldChange(event);
      }}
      noOrdering={options?.noOrdering}
      value={String(ctx.values[fieldData.fieldName])}
      error={
        ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
          ? ctx.errors[fieldData.fieldName]?.toString()
          : null
      }
      disabled={fieldData.getInputState(ctx) === 'disabled'}
    />
  );
}

export function createToggleField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): React.JSX.Element {
  return (
    <Box jc="fe" pt="sm" pb="sm">
      <Toggle
        name={fieldData.fieldName}
        onBlur={ctx.handleBlur}
        onChange={ctx.handleToggleChange}
        value={Boolean(ctx.values[fieldData.fieldName])}
        disabled={fieldData.getInputState(ctx) === 'disabled'}
      />
    </Box>
  );
}

export function createTextField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  preset: keyof TextFieldPresets = 'text',
  icon?: Icons,
  handleBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void,
  onRefChange?: (ref: React.RefObject<HTMLInputElement | null>) => void,
  handleFieldChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
): React.JSX.Element {
  return (
    <TextField
      maxLength={fieldData.maxLength}
      showcaseLength={fieldData.showcaseLength}
      variant="plain"
      preset={preset}
      placeholder={fieldData.placeholder}
      name={fieldData.fieldName}
      onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
        ctx.handleBlur(event);
        if (handleBlur !== undefined) handleBlur(event);
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        ctx.handleFieldChange(event);
        if (handleFieldChange !== undefined) handleFieldChange(event);
      }}
      onRefChange={(ref) => {
        if (onRefChange !== undefined) onRefChange(ref);
      }}
      value={String(ctx.values[fieldData.fieldName] || '')}
      error={
        ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
          ? ctx.errors[fieldData.fieldName]?.toString()
          : null
      }
      elementLeft={icon && <Icon icon={icon} size="sm" />}
      disabled={fieldData.getInputState(ctx) === 'disabled'}
    />
  );
}

export function createCheckBoxField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  variant?: 'default' | 'rounded' | 'border',
  handleBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void,
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
): React.JSX.Element {
  return (
    <vp.Checkbox
      key={fieldData.fieldName}
      name={fieldData.fieldName}
      variant={variant || 'default'}
      value={Boolean(ctx.values[fieldData.fieldName] || false)}
      onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
        if (handleBlur !== undefined) handleBlur(event);
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        ctx.handleFieldChange(event);
        if (handleChange !== undefined) handleChange(event);
      }}
      label=""
    />
  );
}

export function createTextAreaField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  handleBlur?: (event: React.FocusEvent<HTMLTextAreaElement, Element>) => void,
  handleChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void,
): React.JSX.Element {
  return (
    <vp.TextArea
      placeholder={fieldData.placeholder}
      name={fieldData.fieldName}
      value={String(ctx.values[fieldData.fieldName] || '')}
      onBlur={(event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
        if (handleBlur !== undefined) handleBlur(event);
      }}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
        ctx.handleAreaChange(event);
        if (handleChange !== undefined) handleChange(event);
      }}
      maxLength={fieldData.maxLength}
      showcaseLength={fieldData.showcaseLength || false}
      resizeValue={fieldData.resizeValue || 'none'}
    />
  );
}

export function createToolTip(
  information: string,
  type: 'hover' | 'dialogue' = 'hover',
  icon: 'info' | 'question' = 'question',
  position: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' = 'right',
): React.JSX.Element {
  return <vp.Tooltip type={type} information={information} position={position} hasArrow={false} icon={icon} />;
}

export function createLinkField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  linkName: string,
): React.JSX.Element {
  return (
    <div style={{ textAlign: 'start' }}>
      <a
        href={String(ctx.values[fieldData.fieldName] || '')}
        id={fieldData.fieldName}
        target={fieldData.target}
        rel={fieldData.fieldName}
      >
        {linkName}
      </a>
    </div>
  );
}

export function createFileUpload<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  maxSize: number,
  height = '50px',
  handleBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void,
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
): React.JSX.Element {
  return (
    <vp.UploadFile
      id={fieldData.fieldName}
      key={fieldData.fieldName}
      name={fieldData.fieldName}
      idFileName={fieldData.fieldName}
      maxMBSize={maxSize}
      height={height}
      validExtensions={fieldData?.accept?.split(',')}
      base64
      buttonBackgroundColor="#FFD147"
      buttonTextColor="#2D2D2D"
      onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
        if (handleBlur !== undefined) handleBlur(event);
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        ctx.handleFieldChange(event);
        if (handleChange !== undefined) handleChange(event);
      }}
    />
  );
}

export function createTextGoogleAddressFeild<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  preset: keyof TextFieldPresets = 'text',
  icon?: Icons,
): React.JSX.Element {
  const validCountries = Object.keys(fieldData?.getGoogleCountries ? fieldData?.getGoogleCountries(ctx) : {});
  return (
    <GoogleAddressField
      variant="plain"
      ISOCountry={validCountries}
      preset={preset}
      placeholder={fieldData.placeholder}
      name={fieldData.fieldName}
      onBlur={ctx.handleBlur}
      onChange={ctx.handleFieldChange}
      value={String(ctx.values[fieldData.fieldName] || '')}
      error={
        ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
          ? ctx.errors[fieldData.fieldName]?.toString()
          : null
      }
      elementLeft={icon && <Icon icon={icon} size="sm" />}
      disabled={fieldData.getInputState(ctx) === 'disabled'}
      onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
        if (fieldData.handleGoogleResults) {
          fieldData.handleGoogleResults(ctx, addressComponents);
        }
      }}
    />
  );
}

export function useValidationMessages() {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseFields.ValidationMessages' });

  return {
    invalidEmail: t('invalidEmail', { defaultValue: 'This email address is not valid.' }),
    reserveToActivate: t('reserveToActivate', { defaultValue: 'Please enter a fee reserve amount.' }),
    minOneDollar: t('minOneDollar', { defaultValue: 'Please enter a value of at least $1.00.' }),
    minOne: t('minOne', { defaultValue: 'Please enter a value of at least 1.' }),
    missingEmail: t('missingEmail', { defaultValue: 'Please enter an email address.' }),
    noSpecialChars: t('noSpecialChars', { defaultValue: 'Special characters are not enabled for this field.' }),
    minAmount: t('minAmount', { defaultValue: 'Please enter a minimum amount.' }),
    maxAmount: t('maxAmount', { defaultValue: 'Please enter a maximum amount.' }),
    fee: t('fee', { defaultValue: 'Please enter a fee amount.' }),
    percentage: t('percentage', { defaultValue: 'Please enter a percentage.' }),
    numbers: t('numbers', { defaultValue: 'Non numeric characters are not enabled for this field.' }),
  };
}

export const matchers = {
  alpha: 'a-z',
  numeric: '0-9',
  frenchChars: 'àâçéèêëîïôûùüÿñæœ',
  whitespace: ' ',
  specialChars: "&\\-()@'",
  hyphen: '-',
};

export function buildMatcher(_matchers: string[]): string {
  return `^[${_matchers.reduce((accumulator, current) => `${accumulator}${current}`, '')}]*$`;
}
